/* ============================================================================
   THE APP FEED, MOVED INTO OUR MARQUEE

   One job: wait for Instafeed to paint its tiles, then move them into the two
   tracks of our own right-to-left rail so the live feed runs as one row of big
   pictures instead of its own static two-row grid.

   WHY MOVED AND NOT REDRAWN. Every tile holds a <video>, the app's own lazy
   image swap and its lightbox handler. Rebuilding a tile from its
   data-instafeed-thumbnail attribute would keep the picture and throw all three
   away — the video would stop being a video and the click would stop opening
   anything. Moving the node keeps the app's behaviour and adds ours.

   WHY TRACK TWO IS A CLONE. The seamless loop needs one copy of the row in each
   of two sibling tracks; the second is `cloneNode(true)`.

   ==========================================================================
   THE BUG THAT WAS HERE, AND IT WAS HALF THE RAIL
   ==========================================================================

   Reported as: "after the post where he is lying down there are just some white
   photos, and then it refreshes again."

   Both halves of that sentence are one defect. `cloneNode(true)` copies
   attributes, not observers — and EVERY PICTURE IN THIS FEED ARRIVES THROUGH AN
   OBSERVER. Read out of the app's own bundle (method H, and ft/x under it):

     H() { const t = document.querySelectorAll(".instafeed-lazy-image");
           ... const n = new IntersectionObserver(..., {rootMargin:"200px 0px"});
           t.forEach(t => t.classList.contains("...--handled") || n.observe(t)); }

   — a document-wide snapshot taken ONCE, at the app's render, which is before
   our clone exists. Nothing ever observes the clone, so nothing ever runs the
   swap on it. Same story for the videos: the poster is written by a second
   observer of theirs, `e.setAttribute("poster", e.getAttribute("data-poster"))`.

   So the clone shipped with only the app's placeholders in it, permanently:

     img  src = //instafeed.nfcube.com/assets/img/placeholder.png   (1×1, HTTP 200)
          data-instafeed-src = the real photograph, never promoted
     video poster = (none), readyState 0, videoWidth 0 — a blank box
          data-poster = the real thumbnail, never promoted

   MEASURED ON THE LIVE HOME PAGE, 21 Aug 2026. Track one: 16 of 16 photographs
   real, naturalWidth 1080–3072. Track two: 0 of 29 lazy images real, every one
   at naturalWidth 1, and 0 of 3 videos with a poster. At 390×844, where the app
   hands over 4 tiles rather than 16: track one reached 7/7 handled 9.4s after
   the rail entered view, track two stayed 0/7 for the life of the page.

   That is why the row read as "white photos, then it refreshes": on a phone the
   rail is 4 real tiles followed by 4 blank ones followed by the loop point. The
   "refresh" is the seam working exactly as designed — it only READS as a reload
   because the only thing to perceive in the second half was the restart. There
   is no second render pass, no re-firing observer and no third track: measured,
   fill() runs once at ~405ms, the app's container is left with 0 items, and a
   resize does not re-enter it.

   THE PREVIOUS COMMENT HERE HAD THE RIGHT FACT AND THE WRONG CONCLUSION. It
   said the clone is "inert — its lightbox handlers do not come with it. That is
   correct rather than unfortunate", on the reasoning that the whole subtree is
   aria-hidden so nothing in it is meant to be operated. True of the lightbox.
   Not true of the lazy loader, which is not an interaction — it is how the
   picture gets there at all.

   THE FIX IS THE APP'S OWN SWAP, RUN BY US, ON THE COPIES WE MADE. Not a
   rebuild: the same element the app created, the same URL the app parked on it,
   the same --handled class it marks them with so a later pass of theirs skips
   what we already did. Only track two is touched. Track one is left to the app's
   observers because they demonstrably work — 16/16 above.

   AND IT COSTS NO BYTES. The clone's URL is byte-identical to the original's, so
   promoting it early does not add a request over the life of the page; it moves
   one a few seconds earlier and the original then hits cache. The count is what
   the app already loads for track one: 4 pictures at 390px, 16 at 2560px.

   IT STILL FAILS BY LEAVING THEIR GRID ALONE. If the tile class ever changes, no
   tiles are found, the marquee stays hidden and the app's own container stays
   visible. A wrong assumption about somebody else's markup should cost the
   animation, not the feed.
   ========================================================================= */
(() => {
  /* The app's own tile. One class name is the entire coupling surface, and it is
     checked rather than assumed — see the failure note above. */
  const TILE = '.instafeed-new-layout-item';

  /* THE APP'S TWO DATA ATTRIBUTES, AND THE CLASS IT MARKS A DONE IMAGE WITH.
     All three are read out of its bundle rather than guessed; if a release
     renames one, the clone goes back to being blank and nothing else breaks,
     which is the failure mode to want here. */
  const LAZY_SRC = 'data-instafeed-src';
  const LAZY_POSTER = 'data-poster';
  const HANDLED = 'instafeed-lazy-image--handled';

  /* Exactly what the app's own ft() does, and nothing more. Both loops cover the
     overlay badges as well as the photograph — the video marker and the
     Instagram glyph are lazy images too, so a clone without this carries two
     1×1 placeholders where its badges should be. */
  function hydrate(tile) {
    for (const img of tile.querySelectorAll('img[' + LAZY_SRC + ']')) {
      const real = img.getAttribute(LAZY_SRC);
      if (!real) continue;
      img.setAttribute('src', real);
      img.classList.add(HANDLED);
    }

    /* POSTER ONLY, NO PLAYBACK. The copy exists to make the loop point
       invisible; its poster IS the post's own Instagram thumbnail, so the tile
       shows the real picture. A second decoded stream of the same reel, for an
       aria-hidden duplicate that is off-screen most of a pass, is battery for a
       difference nobody can compare side by side — the two copies of one tile
       are a full track width apart (1280px on a phone against a 390px window),
       so they are never in one viewport. */
    for (const video of tile.querySelectorAll('video[' + LAZY_POSTER + ']')) {
      if (video.getAttribute('poster')) continue;
      /* THE SELECTOR MATCHES AN EMPTY ATTRIBUTE, so the value has to be checked.
         video[data-poster] is satisfied by data-poster="", and copying that empty
         string into `poster` makes the browser resolve it against the document and
         fetch the page's own HTML as an image. It never decodes, so the tile is
         blank — and the image loop above has guarded against exactly this since it
         was written. */
      const still = video.getAttribute(LAZY_POSTER);
      if (!still) continue;
      video.setAttribute('poster', still);
    }
  }

  /* ----------------------------------------------------------------------
     THE STREAM GOES, THE PICTURE STAYS.

     MEASURED, phone, home page, cold, scrolled to the end: two tiles in the
     visible track were playing (readyState 4, currentTime 2.6 s and 6.9 s) and
     had pulled 3.88 MB and 1.66 MB. That is 5.5 MB of the page's 9.59 MB, spent
     on two boxes 194x259 CSS px in a decorative marquee below the fold.

     preload="none" AND autoplay=false WERE ALREADY ON ALL SIX VIDEOS, so this
     was never a missing attribute. The app marks them `auto-loop-videos` and
     calls play() itself, and preload only governs what is fetched BEFORE
     playback is requested. To stop the bytes the stream has to be gone.

     THE POSTER IS PROMOTED FIRST AND THAT ORDER IS THE WHOLE CARE. The app
     parks the real thumbnail on data-poster and promotes it on its own schedule;
     removing src before a poster exists would leave a black box rather than a
     photograph. So: poster first, then the stream.

     src IS KEPT ON data-cooper-video rather than discarded, so nothing is
     destroyed and the attribute can be handed back.

     IDEMPOTENT, BECAUSE THE APP RE-RENDERS ON RESIZE and fill() runs again
     after it. A video already silenced has no src to take.
     -------------------------------------------------------------------- */
  const KEPT_SRC = 'data-cooper-video';

  function silence_videos(root) {
    if (!root) return;
    for (const video of root.querySelectorAll('video')) {
      const parked = video.getAttribute(LAZY_POSTER);
      if (parked && !video.getAttribute('poster')) video.setAttribute('poster', parked);

      /* NOTHING TO FALL BACK ON MEANS NOTHING TO TAKE AWAY.

         The poster was promoted conditionally and the stream removed
         unconditionally, so a reel whose data-poster had not been parked yet ended
         up with neither: readyState 0, videoWidth 0, a flat grey box for the life
         of the page. This file's own header describes that state as the one to
         avoid, and the code walked straight into it.

         Such a tile is left as a working video instead. It costs its stream, which
         is the trade this function exists to refuse — but a tile that plays is
         worth more than a tile that is blank, and the next pass silences it once
         the app has parked a thumbnail on it. */
      if (!video.getAttribute('poster')) continue;

      const live = video.getAttribute('src');
      if (live) {
        video.setAttribute(KEPT_SRC, live);
        video.removeAttribute('src');
      }
      for (const source of video.querySelectorAll('source')) source.remove();

      video.removeAttribute('autoplay');
      video.preload = 'none';
      /* THE APP'S OWN HOOK. It selects on this class to start the loop, so
         leaving it on invites a play() on every re-render for a stream that is
         no longer there -- harmless but noisy in the log. */
      video.classList.remove('auto-loop-videos');

      /* pause() stops the intent; load() aborts a fetch already in flight and
         resets the element to its poster. Both are guarded because a detached
         or half-built element can throw on either. */
      try { video.pause(); } catch (e) { /* not playable, nothing to stop */ }
      try { video.load(); } catch (e) { /* nothing to reset */ }
    }
  }

  /* IDS DO NOT SURVIVE A COPY, AND THIS IS NOT TIDINESS. Their video carries
     id="inline-video-<media id>-insta-feed" and the app resolves it with
     document.getElementById(`inline-video-${id}-${target}`). cloneNode brought
     the id along, so the document held two nodes under one id — measured, three
     of them per track — and getElementById answers with whichever comes first in
     document order. Track one wins that today only because it is written first.
     Stripped, their lookup can only ever find the node they created. */
  function strip_ids(tile) {
    tile.removeAttribute('id');
    for (const node of tile.querySelectorAll('[id]')) node.removeAttribute('id');
  }

  /* WHY THE COPIES WAIT NOW, AND WHAT THEY WAIT FOR.

     hydrate() used to run inline inside the clone loop, on every copy as it was
     made. Promoting `data-instafeed-src` to a real `src` on an image carrying no
     loading attribute is an immediate fetch, so the whole rail's photography
     came down the moment the feed painted, with the section 3386px below the
     fold of a 390x844 phone. Measured on the live page, Fast 4G, no scroll:
     4 requests to scontent.cdninstagram.com, 1175.5 KB, starting at 808ms —
     while track one, which the app's OWN IntersectionObserver governs, sat at
     0 of 6 real with every image still on its 1x1 placeholder.png. The app was
     already lazy. Every one of those bytes was ours, and this is the fix.

     THE APP'S OWN OBSERVER IS THE MODEL, not something to race. Its rootMargin
     is "200px 0px" (read out of its bundle, quoted in the header above). This
     uses 400px deliberately, so a copy is never the blank half of a pair: the
     clones promote slightly AHEAD of the originals, which is the safe side of
     exactly the race the old inline call was trying to win by being early.

     OBSERVED ON THE RAIL, NOT ON A TILE, and the section header says why — a
     zero-area target never satisfies an observer. By the time this runs the rail
     is filled and has real height, and the rail is also the one node guaranteed
     to live as long as the feed does, while tiles are replaced wholesale every
     time the app re-renders on resize.

     NO OBSERVER MEANS HYDRATE NOW. A browser without IntersectionObserver gets
     precisely the old behaviour, which was never wrong — only early. */
  /* BOTH TRACKS, NOT JUST THE COPIES — and the comment this replaces was right
     when it was written and wrong now.

     It said track one could be left to the app's own per-tile
     IntersectionObserver "because they demonstrably work — 16/16". That held for
     a static grid. Our marquee translates the track continuously, so the app's
     observer is firing against a moving target: measured on the live strip, 3 of
     16 tiles were still showing the app's 1x1 placeholder, and a minute later all
     16 had filled in. Not permanently empty — LATE. In a row that never stops
     moving, late reads as blank squares sliding past, which is what the owner
     reported.

     STILL GATED ON THE RAIL, so the laziness that matters is untouched: nobody
     who never scrolls to the strip fetches any of it. What goes is the per-tile
     race inside a track that is already on screen. */
  function hydrate_when_near(rail, tracks) {
    const list = Array.isArray(tracks) ? tracks : [tracks];

    /* THE TRACK ON SCREEN GOES FIRST, THE CLONE FOLLOWS A FRAME LATER.

       Handing both tracks to the hydrator fixed tiles that never loaded at all,
       and replaced that with about 32 requests fired flat in one synchronous loop
       — so the row the visitor is actually looking at competed on equal terms with
       an aria-hidden copy a full track-width away. Same requests, same laziness,
       an order that matches what is on screen. */
    const run = () => {
      for (const tile of [...list[0].children]) hydrate(tile);
      const rest = list.slice(1);
      if (!rest.length) return;
      const later = () => {
        for (const track of rest) for (const tile of [...track.children]) hydrate(tile);
      };
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(later);
      else later();
    };

    if (typeof IntersectionObserver !== 'function') {
      run();
      return;
    }

    /* A re-render replaces the copies wholesale, so an observer left over from
       the previous pass would be watching nodes that have left the document.
       One per rail: disconnect the old one, then watch with the track it owns. */
    if (rail.cooperIgNear) rail.cooperIgNear.disconnect();

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      if (rail.cooperIgNear === observer) rail.cooperIgNear = null;
      run();
    }, {rootMargin: '400px 0px'});

    rail.cooperIgNear = observer;
    observer.observe(rail);
  }

  /* ==========================================================================
     THE SPEED IS THE CONSTANT AND THE DURATION IS DERIVED FROM IT.

     Reported as: "the instagram feed has gone buggy — two panels, the bottom one
     just flies, spinning fast." Both halves of that were one page state, and the
     flying half is the one this function exists for.

     The keyframe translates -100% OF THE TRACK, so px/s is trackWidth / duration
     and the duration was a plain CSS number: 88s, or 20s under the 767px media
     query. Those two numbers were chosen against the two track widths the app
     hands over at those two window sizes, which is fine only while the window
     never changes after the fill. It changes.

     MEASURED ON THE LIVE HOME PAGE, 22 Aug 2026, one window dragged from 1440px
     to 700px with no reload:

       before the drag   24 tiles, track 5940px, duration 88s ->  -68 px/s
       after  the drag   24 tiles, track 5940px, duration 20s -> -286 px/s

     The tracks keep the tile count they were filled with — nothing re-fills them
     on a resize — while the media query switches the duration to the one written
     for a 1632px phone track. Four times the intended speed, from a stylesheet
     that cannot see how wide the thing it is animating is.

     THIS IS TRAP G IN ANOTHER COAT. The same defect as measuring a display:none
     track: a number computed from a width that is not the width in force when
     the animation runs. The durable answer is not a different constant duration
     but no constant duration at all — measure the track, divide by the speed we
     actually want, and write the duration as an inline custom property, which
     beats every breakpoint rule in the stylesheet.

     WHY 70 px/s. It is the desktop drift that has been on the page all along:
     5940px over 88s is 67.5 px/s, and the phone's own 1632px over 20s is 81.6.
     One number between them holds both looks and removes the cliff between
     them. The clamps are the guard rails the CSS notes already argued for: never
     a ticker, never a row that reads as still.

     THE CSS NUMBERS STAY where they are, as the no-JavaScript fallback and as
     what the hand-filled rail keeps using. Nothing here shortens a duration to
     hide a symptom: 5940px still takes 84.9s, which is the pass the owner has
     been looking at.
     ====================================================================== */
  const SPEED = 70;
  const MIN_DUR = 24;
  const MAX_DUR = 200;

  function pace(marquee, track) {
    /* A TRACK WITH NO WIDTH IS NOT A MEASUREMENT, IT IS A ZERO. Before the
       wrapper's `hidden` is cleared this box is display:none and reports 0, and
       a duration computed from it would be wrong for the life of the page. So
       the answer to a zero is to write nothing and wait to be called again --
       the observer below is what calls back. */
    const width = track.getBoundingClientRect().width;
    if (!width) return false;

    let secs = width / SPEED;
    if (secs < MIN_DUR) secs = MIN_DUR;
    if (secs > MAX_DUR) secs = MAX_DUR;

    /* ON THE MARQUEE, NOT ON THE TRACK, so both tracks read one number and stay
       in step -- a seam is two tracks moving at exactly the same rate. */
    const next = secs.toFixed(1) + 's';
    if (marquee.style.getPropertyValue('--cooper-ig-dur') !== next) {
      marquee.style.setProperty('--cooper-ig-dur', next);
    }
    return true;
  }

  /* EVERY WAY THE WIDTH CAN CHANGE, WATCHED IN ONE PLACE. The window resizing,
     the app re-rendering at a new tile size, the deferred hydration swapping
     1x1 placeholders for photographs, a re-fill with a different tile count:
     all of them are a width change on the track, and none of them is a resize
     event we could listen for instead. ResizeObserver also fires the moment the
     box stops being display:none, which is exactly the callback the zero above
     is waiting for.

     A browser without ResizeObserver gets one pass plus the window's own resize
     event, which is the common case and not the interesting one. */
  function pace_and_watch(marquee, track) {
    if (marquee.cooperIgPace) marquee.cooperIgPace.disconnect();
    pace(marquee, track);

    if (typeof ResizeObserver !== 'function') {
      if (!marquee.cooperIgPaceFallback) {
        marquee.cooperIgPaceFallback = true;
        addEventListener('resize', () => pace(marquee, track));
      }
      return;
    }

    const observer = new ResizeObserver(() => pace(marquee, track));
    marquee.cooperIgPace = observer;
    observer.observe(track);
  }

  /* THE APP'S OWN GRID, ACTUALLY HIDDEN THIS TIME -- and this is the "two
     panels" half of the report.

     `source.hidden = true` was doing nothing. MEASURED on the live page: the
     attribute is on the element, and getComputedStyle still answers
     display:block, offsetHeight 314. Remove the id and it collapses to
     display:none; put the id back and it is block again; an inline
     `display:none` WITHOUT !important also loses. So the app's own stylesheet
     (instafeed-19.0.25.css, cross-origin, so the rule cannot be read out of the
     CSSOM) carries an id selector with !important, which beats both the UA's
     `[hidden]` rule and our own `.cooper-instagram__app--rail > div[hidden]`
     class-plus-attribute rule in the section. No stylesheet of ours can win
     that without knowing the id, which is a theme setting.

     An inline declaration marked important does win, and it is the only thing
     that does. Without it the app's container sits above our rail at its full
     height forever -- empty at first, and then FULL again the moment the app
     re-renders on a resize: measured, 12 fresh tiles of its own grid in a
     visible box directly above the marquee. That is the second panel.

     BUT NOT display:none, AND THIS COST A PUSH. `display: none !important` was
     the first version of this function and it emptied the rail: measured on the
     live page, every tile in our track went to `width: 0px; height: 0px` and the
     marquee's height to 0. The app does not size a tile once and forget it -- it
     re-measures its container and REWRITES the inline width and height of every
     tile it knows about, and it still knows about ours, which is also how they
     got the 247px they were running at. Take the container out of layout and it
     measures 0, and it hands that 0 to tiles that are no longer inside it.

     SO THE BOX KEEPS ITS WIDTH AND LOSES EVERYTHING ELSE. Still in flow, still
     the full width of the rail, so the app's own measurement is exactly what it
     was; zero height with its overflow clipped, so the second panel has no room
     to appear in; invisible and untouchable, so nothing of it can paint or be
     hit. The app can re-render into it as often as it likes.

     THE ATTRIBUTE STAYS TOO. It is what the markup comment promises and what
     takes the subtree out of the accessibility tree; the declarations are only
     there to make the paint match. */
  function hide_app_grid(source) {
    source.hidden = true;
    source.style.setProperty('height', '0', 'important');
    source.style.setProperty('min-height', '0', 'important');
    source.style.setProperty('overflow', 'hidden', 'important');
    source.style.setProperty('visibility', 'hidden', 'important');
    source.style.setProperty('pointer-events', 'none', 'important');
  }

  function fill(rail) {
    const source = rail.querySelector('[id]');
    const marquees = rail.querySelector('[data-ig-app-marquees]');
    const tracks = [...rail.querySelectorAll('[data-ig-app-track]')];
    if (!source || !marquees || tracks.length < 2) return false;

    const tiles = [...source.querySelectorAll(TILE)];
    if (!tiles.length) return false;

    /* Idempotent: the app re-renders on resize, and this runs again after it. */
    for (const t of tracks) t.textContent = '';

    for (const tile of tiles) {
      /* The app sizes each tile with an inline width and height computed for ITS
         layout. Cleared so our own CSS decides how big a tile is — otherwise a
         row of 418px boxes would keep the grid's proportions inside a rail built
         for something else. */
      tile.style.width = '';
      tile.style.height = '';
      tile.classList.add('cooper-instagram__tile--app');
      tracks[0].append(tile);
    }

    for (const tile of [...tracks[0].children]) {
      const copy = tile.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      strip_ids(copy);
      tracks[1].append(copy);
    }

    /* BEFORE THE TRACK IS UNHIDDEN, so no frame ever exists in which a stream
       is both visible and fetching. Read off the rail rather than assumed, so
       the owner's checkbox governs it and the default here is the safe one for a
       theme rendered without the attribute. */
    if (rail.dataset.igVideoStills !== 'false') {
      silence_videos(tracks[0]);
      silence_videos(tracks[1]);
    }

    hydrate_when_near(rail, tracks);

    /* ORDER MATTERS AND IT IS THE WHOLE POINT OF THIS THREE-LINER. The wrapper
       is unhidden FIRST, because a display:none track measures 0 and pace()
       would have nothing to work from; the duration is set from the width the
       track has once it is on the page; and the app's grid goes last, because
       hiding it is a paint decision that nothing else here depends on. */
    marquees.hidden = false;
    pace_and_watch(tracks[0].parentElement, tracks[0]);
    hide_app_grid(source);
    return true;
  }

  function wire(root) {
    const scope = root instanceof Element ? root : document;

    for (const rail of scope.querySelectorAll('[data-ig-app-rail]:not([data-ig-bound])')) {
      rail.dataset.igBound = '';

      /* THE APP PAINTS WHEN IT PAINTS. It is a third-party script with its own
         network round trip, so there is nothing to await — this watches its
         container instead and stops as soon as it has something, with a ceiling
         so a feed that never arrives does not leave an observer running for the
         life of the page. */
      const source = rail.querySelector('[id]');
      if (!source) continue;

      if (fill(rail)) continue;

      let settled = false;
      const observer = new MutationObserver(() => {
        if (settled) return;
        if (fill(rail)) {
          settled = true;
          observer.disconnect();
        }
      });
      observer.observe(source, {childList: true, subtree: true});

      /* Thirty seconds is far past any plausible feed; after that their grid is
         the right thing to be showing. */
      setTimeout(() => {
        if (!settled) observer.disconnect();
      }, 30000);
    }
  }

  wire(document);
  document.addEventListener('shopify:section:load', (event) => wire(event.target));
})();
